<template>
  <div :class="['select-recipients', currentECircular.type]">
    <div class="table-header">
      <span class="tree-arrow" />
      <template v-if="isTenantEcircular">
        <span class="tree-zone">{{ $t('eCircular.floor') }}</span>
        <span class="tree-trade-name">{{ $t('eCircular.tradeName') }}</span>
        <span
          v-if="showUserTypeCol"
          class="tree-contact-types"
        >{{ $t('userType.name') }}</span>
        <span class="tree-premises">{{ $t('eCircular.premises') }}</span>
        <span
          v-if="showMarketingType"
          class="tree-marketing-type"
        >
          {{ $t('tenancy.marketingType') }}
        </span>
      </template>
      <template v-if="isVendorEcircular">
        <span class="tree-company-id">{{ $t('tenancy.companyId') }}</span>
        <span class="tree-company-name">{{ $t('eCircular.companyName') }}</span>
        <span
          class="tree-recipient-type"
          style="padding-left:15px;"
        >
          {{ $t('userType.name') }}
        </span>
      </template>
      <template v-if="isTenancyEcircular">
        <span class="tree-recipient-type">{{ $t('eCircular.recipientType.label') }}</span>
        <span class="tree-trade-name">{{ $t('eCircular.tradeName') }}</span>
      </template>
      <i
        v-if="isAddOperation && treeData.length"
        :class="renderTableSelectIcon()"
        @click="selectAllTrees"
      />
      <span
        v-if="isDelOperation"
        class="operate-placeholder"
      />
    </div>
    <RecipientsTree
      v-for="list in treeData"
      :key="list.id"
      :list="list"
      :eCircularType="currentECircular.type"
      :selectOneLeaf="selectOneLeaf"
      :selectOneRoot="selectOneRoot"
      :operate="operate"
      :showMarketingType="showMarketingType"
      :expanded="list.expanded"
      :deleteOneRoot="deleteOneRoot"
      :deleteOneLeaf="deleteOneLeaf"
      @updateExpanded="(val) => { list.expanded = val }"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import { TREE_STATUS, operationType } from '@/utils/consts'
import EventBus from '@/utils/eventBus'
import RecipientsTree from './RecipientsTree'
import { calcRootStatus, emailUniqueKey, prepareTenantTreeData } from './helper'
import { useSelectRecipientsMix } from './hook'

const DELETE_ONE_LEAF = 'DELETE_ONE_LEAF'
const DELETE_ONE_ROOT = 'DELETE_ONE_ROOT'

export default {
  name: 'SelectRecipients',
  components: {
    RecipientsTree,
  },
  props: {
    operate: {
      type: String,
      default: operationType.ADD, // DEL
    },
    showMarketingType: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const mixSrHooks = useSelectRecipientsMix(props)
    return {
      ...mixSrHooks,
      operationType,
    }
  },
  data () {
    return {
      treeData: [],
      tableStatus: '',
      TREE_STATUS,
      rawLists: {},
      selfAddedRecipients: [],
    }
  },
  computed: {
    ...mapGetters('ecircular', ['currentECircular', 'searchRecipientsResult', 'addedRecipients', 'navigatingToRecipientOwner']),
  },
  watch: {
    addedRecipients () {
      if ([operationType.DEL, operationType.VIEW].includes(this.operate)) {
        this.initTreeStatus()
      }
    },
  },
  mounted () {
    if (this.operate === operationType.ADD) {
      EventBus.$on(DELETE_ONE_LEAF, this.receiveDeleteOneLeaf)
      EventBus.$on(DELETE_ONE_ROOT, this.receiveDeleteOneRoot)
    }

    this.initTreeStatus()
  },
  destroyed () {
    if (this.operate === operationType.ADD) {
      EventBus.$off(DELETE_ONE_LEAF)
      EventBus.$off(DELETE_ONE_ROOT)
      if (!this.navigatingToRecipientOwner) {
        // Clear page-level search state only when the whole ADD tree is destroyed.
        this.clearSearchConditions({})
        this.clearVendorSearchConditions({})
        this.updateSearchTreesStatus([])
      }
    }
  },
  methods: {
    ...mapMutations('ecircular', {
      updateSearchTreesStatus: mutationTypes.ECIRCULAR_SAVE_SEARCH_RECIPIENTS,
      updateAddedRecipients: mutationTypes.ECIRULAR_ADDED_RECIPIENTS,
      clearSearchConditions: mutationTypes.ECIRCULAR_SAVE_SEARCH_RECIPIENT_CONDITIONS,
      clearVendorSearchConditions: mutationTypes.ECIRCULAR_SAVE_SEARCH_VENDOR_RECIPIENT_CONDITIONS,
    }),
    // select logic
    selectAllTrees () {
      if (this.tableStatus === TREE_STATUS.selected) {
        this.setAllLeaves(TREE_STATUS.unselected)
      } else {
        this.setAllLeaves(TREE_STATUS.selected)
      }
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    setAllLeaves (status) {
      this.treeData.map(root => {
        this.setOneTreeLeaveStatus(root, status)
        calcRootStatus(root)
      })
    },
    calcTableStatus () {
      const length = this.treeData.length
      let selectNum = 0
      let selectPartNum = 0
      this.treeData.map(root => {
        if (root.selected === TREE_STATUS.selectPart) {
          selectPartNum++
        } else if (root.selected === TREE_STATUS.selected) {
          selectNum++
        }
      })
      if (selectPartNum) {
        this.tableStatus = TREE_STATUS.selectPart
      } else if (selectNum === length) {
        this.tableStatus = TREE_STATUS.selected
      } else if (selectNum === 0) {
        this.tableStatus = TREE_STATUS.unselected
      } else {
        this.tableStatus = TREE_STATUS.selectPart
      }
    },
    initTreeStatus () {
      const list = lodash.cloneDeep(this.operate === operationType.ADD ? this.searchRecipientsResult : this.addedRecipients)
      this.treeData = prepareTenantTreeData(list, this.currentECircular.type, this.operate !== operationType.ADD)
      const addedHash = {}
      const addedHashRootIds = []
      this.addedRecipients.map(root => {
        const arr = []
        root.recipients.map(leaf => {
          arr.push(emailUniqueKey(leaf))
        })
        addedHashRootIds.push(root.id)
        addedHash[root.id] = arr
      })
      this.treeData.map(root => {
        if (addedHashRootIds.includes(root.id)) {
          const hashMap = addedHash[root.id]
          root.recipients.map(leaf => {
            if (hashMap.includes(emailUniqueKey(leaf))) {
              this.$set(leaf, 'selected', TREE_STATUS.added)
            } else {
              this.$set(leaf, 'selected', TREE_STATUS.unselected)
            }
          })
        } else {
          root.recipients.map(leaf => {
            this.$set(leaf, 'selected', TREE_STATUS.unselected)
          })
        }
        this.$set(root, 'selected', TREE_STATUS.unselected)
        calcRootStatus(root)
      })
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    _updateSearchTreesStatus () {
      if (this.operate !== operationType.ADD) return

      this.updateSearchTreesStatus(lodash.cloneDeep(this.treeData))
    },
    getTreeLeafFilterKey (root, leaf) {
      return [root && root.id, leaf && leaf.id, emailUniqueKey(leaf)].join('|')
    },
    buildTreeLeafFilterKeySet (trees = []) {
      const keySet = new Set()
      ;(trees || []).forEach((root) => {
        ;(root.recipients || []).forEach((leaf) => {
          keySet.add(this.getTreeLeafFilterKey(root, leaf))
        })
      })
      return keySet
    },
    syncSelectionAfterFilterPrune (remainingSelectableTrees = []) {
      const remainingKeys = this.buildTreeLeafFilterKeySet(remainingSelectableTrees)
      const addedKeys = this.buildTreeLeafFilterKeySet(this.addedRecipients)

      this.treeData.forEach((root) => {
        ;(root.recipients || []).forEach((leaf) => {
          const key = this.getTreeLeafFilterKey(root, leaf)
          if (addedKeys.has(key)) {
            this.$set(leaf, 'selected', TREE_STATUS.added)
          } else if (
            leaf.selected === TREE_STATUS.added ||
            (leaf.selected === TREE_STATUS.selected && !remainingKeys.has(key))
          ) {
            this.$set(leaf, 'selected', TREE_STATUS.unselected)
          }
        })
        calcRootStatus(root)
      })
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    selectOneLeaf (leaf, root) {
      if (leaf.selected === TREE_STATUS.added) {
        return
      }
      if (leaf.selected === TREE_STATUS.selected) {
        leaf.selected = TREE_STATUS.unselected
      } else {
        leaf.selected = TREE_STATUS.selected
      }
      calcRootStatus(root)
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    setOneTreeLeaveStatus (root, status) {
      root.recipients.map(leaf => {
        if (leaf.selected !== TREE_STATUS.added) {
          leaf.selected = status
        }
      })
    },
    selectOneRoot (root) {
      if (root.selected === TREE_STATUS.selected) {
        this.setOneTreeLeaveStatus(root, TREE_STATUS.unselected)
      } else {
        this.setOneTreeLeaveStatus(root, TREE_STATUS.selected)
      }
      calcRootStatus(root)
      this.calcTableStatus()
    },
    // add to addedRecipients
    _addToAddedRecipients () {
      this.selfAddedRecipients = lodash.cloneDeep(this.addedRecipients)
      this.treeData.map(root => {
        if (root.selected !== TREE_STATUS.unselected) {
          this.addtoAddedRecipients(root)
        }
      })
      this.updateAddedRecipients(this.selfAddedRecipients)
    },
    isExistRootInAddedRecipients (rootId) {
      for (let i = 0, len = this.selfAddedRecipients.length; i < len; i++) {
        if (rootId === this.selfAddedRecipients[i].id) {
          return true
        }
      }
      return false
    },
    addtoAddedRecipients (root) {
      if (!this.isExistRootInAddedRecipients(root.id)) {
        this.addRootToAddedRecipients(root)
      } else {
        this.addLeavesToAddedRecipients(root)
      }
    },
    addRootToAddedRecipients (root) {
      const newRoot = lodash.cloneDeep(root)
      newRoot.recipients.length = 0
      root.recipients.map(leaf => {
        if (!this.isVendorEcircular) {
          leaf.tradeName = root.tradeName
        }
        if (leaf.selected !== TREE_STATUS.unselected) {
          leaf.selected = TREE_STATUS.added
          newRoot.recipients.push(leaf)
        }
      })
      this.selfAddedRecipients.push(newRoot)
    },
    addLeavesToAddedRecipients (newRoot) {
      this.selfAddedRecipients.map(root => {
        if (root.id === newRoot.id) {
          newRoot.recipients.map(leaf => {
            const isExist = root.recipients.findIndex(r => emailUniqueKey(leaf) === emailUniqueKey(r))
            if (isExist < 0) {
              leaf.tradeName = root.tradeName
              if (leaf.selected !== TREE_STATUS.unselected) {
                leaf.selected = TREE_STATUS.added
                root.recipients.push(leaf)
              }
            }
          })
        }
      })
    },
    // delete functions
    receiveDeleteOneLeaf ({delLeaf, delRoot}) {
      this.treeData.map(root => {
        if (root.id === delRoot.id) {
          root.recipients.map(leaf => {
            if (emailUniqueKey(leaf) === emailUniqueKey(delLeaf)) {
              leaf.selected = TREE_STATUS.unselected
            }
          })
          calcRootStatus(root)
        }
      })
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    receiveDeleteOneRoot ({delRoot}) {
      this.treeData.map(root => {
        if (root.id === delRoot.id) {
          root.recipients.map(leaf => {
            if (leaf.selected === TREE_STATUS.added) {
              leaf.selected = TREE_STATUS.unselected
            }
          })
          calcRootStatus(root)
        }
      })
      this.calcTableStatus()
      this._updateSearchTreesStatus()
    },
    deleteOneRoot (delRoot) {
      const index = lodash.findIndex(this.treeData, root => {
        return root.id === delRoot.id
      })
      this.treeData.splice(index, 1)
      this.updateAddedRecipients(lodash.cloneDeep(this.treeData))
      EventBus.$emit(DELETE_ONE_ROOT, {delRoot})
    },
    deleteOneLeaf (delLeaf, delRoot) {
      delLeaf.selected = TREE_STATUS.unselected
      if (delRoot.recipients.length === 1) {
        const index = lodash.findIndex(this.treeData, root => {
          return root.id === delRoot.id
        })
        this.treeData.splice(index, 1)
        // addRecipients changed， need to change searchRecipientsResult
        this.updateAddedRecipients(lodash.cloneDeep(this.treeData))
        EventBus.$emit(DELETE_ONE_ROOT, {delRoot})
      } else {
        this.treeData.forEach(root => {
          if (root.id === delRoot.id) {
            const index = lodash.findIndex(root.recipients, leaf => {
              return emailUniqueKey(leaf) === emailUniqueKey(delLeaf)
            })
            root.recipients.splice(index, 1)
          }
        })
        // addRecipients changed， need to change searchRecipientsResult
        this.updateAddedRecipients(lodash.cloneDeep(this.treeData))
        EventBus.$emit(DELETE_ONE_LEAF, {delLeaf, delRoot})
      }
    },
    // render select icons
    renderTableSelectIcon () {
      let cls = ''
      switch (this.tableStatus) {
        case TREE_STATUS.selected:
          cls = 'selected'
          break
        case TREE_STATUS.unselected:
          cls = 'unselected'
          break
        case TREE_STATUS.selectPart:
          cls = 'select-part'
          break
      }
      return `${cls} select`
    },
  },
}
</script>

<style scoped lang='scss'>
@import "~styles/recipientsTree";

.select-recipients {
  .table-header {
    display: flex;
    align-items: center;
    height: 37px;
    line-height: 37px;
    font-family: Verdana;
    font-size: 14px;
    font-weight: bold;
    border: solid 1px #ededed;
    span {
      text-transform: uppercase;
      line-height: 1.43;
      &.tree-marketing-type {
        text-transform: none;
      }
    }
  }
  .operate-placeholder {
    display: inline-block;
    width: 20px;
  }
}

</style>
